import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authApi } from "@/lib/api";
import { Sparkles, Lock, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    if (!token) return setError("Missing token. Request a new reset link.");

    setBusy(true);
    try {
      await authApi.reset(token, password);
      setDone(true);
      toast.success("Password updated ✦ sign in with the new one");
      setTimeout(() => nav("/", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen aurora relative overflow-hidden grain flex items-center justify-center p-6">
      <div className="pointer-events-none absolute -top-16 -left-16 w-72 h-72 bg-neon-pink/40 blur-3xl slime-blob" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-96 h-96 bg-neon-purple/40 blur-3xl slime-blob" />

      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md glass rounded-[2rem] p-8 sticker"
        style={{ boxShadow: "0 0 60px rgba(255,0,127,0.3)" }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-6 h-6 text-neon-pink animate-pulse" />
          <span className="font-display text-2xl font-bold chrome-text">New password</span>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="reset-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-neon-lime/20 flex items-center justify-center mb-3 neon-glow-lime">
              <Check className="w-7 h-7 text-neon-lime" />
            </div>
            <div className="font-display text-2xl font-bold">Password updated</div>
            <p className="text-white/70 text-sm mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="text-white/70 text-sm mb-2">Choose a new password. We'll sign you out of every device for safety.</p>
            <Field icon={Lock}>
              <Input data-testid="reset-password" type="password" required minLength={8} autoComplete="new-password"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="at least 8 characters"
                className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12 pl-10" />
            </Field>
            <Field icon={Lock}>
              <Input data-testid="reset-confirm" type="password" required minLength={8} autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="confirm password"
                className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl h-12 pl-10" />
            </Field>
            {error && (
              <div data-testid="reset-error" className="rounded-xl border border-neon-pink/40 bg-neon-pink/10 text-neon-pink px-3 py-2 text-sm">
                {error}
              </div>
            )}
            <Button data-testid="reset-submit" type="submit" disabled={busy}
              className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 text-white font-bold py-6 neon-glow-pink">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving…</> : "Update password"}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}

function Field({ icon: Icon, children }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
      {children}
    </div>
  );
}
